import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Globe, Trash2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { authAPI, accountAPI } from '../services/api';
import { useT } from '../services/i18n';
import ConnectedDevices from './ConnectedDevices';

const Feedback = ({ state, testid }) => state ? (
  <p data-testid={testid} className={`text-sm flex items-center gap-2 ${state.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
    {state.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {state.text}
  </p>
) : <span />;

const errorText = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x.msg).join(' ');
  return d || fallback;
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <Input data-testid={testid} {...props} />
  </div>
);

const AccountSettings = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [pwdMsg, setPwdMsg] = useState(null);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/connexion?redirect=/mon-compte/parametres');
      return;
    }
    authAPI.getProfile().then(p => {
      setProfile(p);
      setForm({
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        phone: p.phone || '',
        newsletter: !!p.newsletter,
        doc_lang: p.doc_lang || 'fr',
        address: { address: '', addressComplement: '', postalCode: '', city: '', country: 'France', ...(p.address || {}) },
      });
    }).catch(() => navigate('/connexion?redirect=/mon-compte'));
  }, [navigate]);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const setAddr = (key) => (e) => setForm(f => ({ ...f, address: { ...f.address, [key]: e.target.value } }));

  const saveProfile = async () => {
    setProfileMsg(null);
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setProfileMsg({ type: 'error', text: 'Le prénom et le nom sont obligatoires' });
      return;
    }
    setSaving(true);
    try {
      const hasAddress = Object.values(form.address).some(v => v && v !== 'France');
      const updated = await authAPI.updateProfile({ ...form, address: hasAddress ? form.address : null });
      setProfile(updated);
      setProfileMsg({ type: 'success', text: 'Profil mis à jour' });
      window.dispatchEvent(new Event('storage'));
    } catch (e) {
      setProfileMsg({ type: 'error', text: errorText(e, 'Erreur lors de la mise à jour') });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setPwdMsg(null);
    if (pwd.next.length < 8) { setPwdMsg({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères' }); return; }
    if (pwd.next !== pwd.confirm) { setPwdMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
    setSavingPwd(true);
    try {
      const r = await authAPI.changePassword(pwd.current, pwd.next);
      setPwd({ current: '', next: '', confirm: '' });
      setPwdMsg({ type: 'success', text: r?.message || 'Mot de passe modifié avec succès' });
    } catch (e) {
      setPwdMsg({ type: 'error', text: errorText(e, 'Erreur lors du changement de mot de passe') });
    } finally {
      setSavingPwd(false);
    }
  };

  const [prefs, setPrefs] = useState(null);
  const [delPwd, setDelPwd] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  useEffect(() => { accountAPI.preferences().then(setPrefs).catch(() => setPrefs({ language: 'fr', currency: 'EUR', order_emails: true, marketing_emails: false })); }, []);
  const savePrefs = async (next) => { setPrefs(next); try { await accountAPI.savePreferences(next); toast.success('Préférences enregistrées'); } catch { toast.error('Enregistrement impossible'); } };
  const deleteAccount = async () => {
    if (!window.confirm('Supprimer définitivement votre compte ? Cette action est irréversible.')) return;
    try { await accountAPI.deleteAccount(delPwd); authAPI.logout(); toast.success('Compte supprimé'); navigate('/'); window.location.reload(); } catch (e) { toast.error(errorText(e, 'Suppression impossible')); }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="account-settings">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white rounded-xl shadow-sm p-6" data-testid="profile-section">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><User className="text-orange-500" size={20} /> {t('acc.personalInfo')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={`${t('checkout.firstName')} *`} testid="profile-first-name" value={form.first_name} onChange={setField('first_name')} />
              <Field label={`${t('checkout.lastName')} *`} testid="profile-last-name" value={form.last_name} onChange={setField('last_name')} />
              <Field label={t('checkout.phone')} testid="profile-phone" value={form.phone} onChange={setField('phone')} placeholder="06 12 34 56 78" />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('checkout.email')}</label>
                <Input value={profile.email} disabled className="bg-gray-50" />
              </div>
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" data-testid="profile-newsletter" checked={form.newsletter} onChange={(e) => setForm(f => ({ ...f, newsletter: e.target.checked }))} className="rounded border-gray-300 text-orange-500" />
              {t('acc.newsletter')}
            </label>
            <label className="block mt-3 text-sm text-gray-700" data-testid="profile-doc-lang-label">
              <span className="block text-xs font-medium text-gray-600 mb-1">{t('acc.docLang')}</span>
              <select data-testid="profile-doc-lang" value={form.doc_lang} onChange={setField('doc_lang')} className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm">
                <option value="fr">Français</option><option value="en">English</option><option value="de">Deutsch</option><option value="es">Español</option>
              </select>
            </label>

            <h2 className="text-lg font-bold text-gray-900 mt-8 mb-4 flex items-center gap-2"><MapPin className="text-orange-500" size={20} /> {t('acc.savedAddress')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Adresse" testid="profile-address" value={form.address.address} onChange={setAddr('address')} placeholder="123 rue de la République" /></div>
              <div className="sm:col-span-2"><Field label="Complément" testid="profile-address-complement" value={form.address.addressComplement} onChange={setAddr('addressComplement')} placeholder="Appartement, étage..." /></div>
              <Field label={t('checkout.postalCode')} testid="profile-postal-code" value={form.address.postalCode} onChange={setAddr('postalCode')} maxLength={5} placeholder="75001" />
              <Field label={t('checkout.city')} testid="profile-city" value={form.address.city} onChange={setAddr('city')} placeholder="Paris" />
            </div>

            <div className="mt-6 flex items-center justify-between gap-4">
              <Feedback state={profileMsg} testid="profile-message" />
              <Button data-testid="profile-save" onClick={saveProfile} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving && <Loader2 size={16} className="mr-2 animate-spin" />} {t('acc.save')}
              </Button>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6 self-start" data-testid="password-section">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Lock className="text-orange-500" size={20} /> {t('acc.password')}</h2>
            <div className="space-y-4">
              <Field label={t('acc.currentPwd')} testid="password-current" type={showPwd ? 'text' : 'password'} value={pwd.current} onChange={(e) => setPwd(p => ({ ...p, current: e.target.value }))} />
              <Field label={t('acc.newPwd')} testid="password-new" type={showPwd ? 'text' : 'password'} value={pwd.next} onChange={(e) => setPwd(p => ({ ...p, next: e.target.value }))} />
              <Field label={t('acc.confirmPwd')} testid="password-confirm" type={showPwd ? 'text' : 'password'} value={pwd.confirm} onChange={(e) => setPwd(p => ({ ...p, confirm: e.target.value }))} />
              <button type="button" data-testid="password-toggle-visibility" onClick={() => setShowPwd(v => !v)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />} {showPwd ? t('acc.hidePwd') : t('acc.showPwd')} {t('acc.pwdWord')}
              </button>
            </div>
            <div className="mt-6 flex items-center justify-between gap-4">
              <Feedback state={pwdMsg} testid="password-message" />
              <Button data-testid="password-save" onClick={savePassword} disabled={savingPwd || !pwd.current || !pwd.next} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
                {savingPwd && <Loader2 size={16} className="mr-2 animate-spin" />} {t('acc.changePwd')}
              </Button>
            </div>
          </section>

        </div>

        <ConnectedDevices />

        {prefs && (
          <section className="bg-white rounded-xl shadow-sm p-6" data-testid="preferences-section">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Globe className="text-orange-500" size={20} /> {t('acc.prefs')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.language')}</label>
                <select data-testid="pref-language" value={prefs.language} onChange={(e) => savePrefs({ ...prefs, language: e.target.value })} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white">
                  {[['fr', 'Français'], ['en', 'English'], ['de', 'Deutsch'], ['es', 'Español'], ['it', 'Italiano']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.currency')}</label>
                <select data-testid="pref-currency" value={prefs.currency} onChange={(e) => savePrefs({ ...prefs, currency: e.target.value })} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white">
                  {[['EUR', '€ Euro'], ['CHF', 'CHF Franc suisse'], ['GBP', '£ Livre sterling']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{t('acc.prefsNote')}</p>
            <div className="mt-4 space-y-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="pref-order-emails" checked={prefs.order_emails} onChange={(e) => savePrefs({ ...prefs, order_emails: e.target.checked })} className="rounded border-gray-300 text-orange-500" /><Bell size={14} className="text-gray-400" /> {t('acc.orderEmails')}</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="pref-marketing-emails" checked={prefs.marketing_emails} onChange={(e) => savePrefs({ ...prefs, marketing_emails: e.target.checked })} className="rounded border-gray-300 text-orange-500" /><Bell size={14} className="text-gray-400" /> {t('acc.marketing')}</label>
            </div>
          </section>
        )}

        <section className="bg-white rounded-xl shadow-sm p-6 border border-red-100" data-testid="delete-account-section">
          <h2 className="text-lg font-bold text-red-700 mb-2 flex items-center gap-2"><Trash2 size={20} /> {t('acc.delete')}</h2>
          <p className="text-sm text-gray-600 mb-3">{t('acc.deleteInfo')}</p>
          {!showDelete ? <Button variant="outline" onClick={() => setShowDelete(true)} data-testid="delete-account-open" className="border-red-300 text-red-700 hover:bg-red-50">{t('acc.deleteOpen')}</Button> : (
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('acc.confirmYourPwd')} testid="delete-account-password" type="password" value={delPwd} onChange={(e) => setDelPwd(e.target.value)} />
              <Button onClick={deleteAccount} disabled={!delPwd} data-testid="delete-account-confirm" className="bg-red-600 hover:bg-red-700">{t('acc.deleteConfirm')}</Button>
              <Button variant="outline" onClick={() => setShowDelete(false)} data-testid="delete-account-cancel">{t('common.cancel')}</Button>
            </div>
          )}
        </section>
    </div>
  );
};

export default AccountSettings;
